import React, { useEffect, useState } from 'react'
import { Container, Row, Col } from 'react-bootstrap'
import { connect } from 'react-redux'
import { TRANSACTION_FAILED, TRANSACTION_PROCESSING, transactionInit } from '../../Pricing/actions'
import { LoadingBox } from '../LoadingBox/LoadingBox'
import { ErrorBox } from '../ErrorBox/ErrorBox'
import { PLANS } from '../../constants/plans'
import { ERROR_CODE_MAP } from '../../stripe.constants'
import { withGoogleReCaptcha } from 'react-google-recaptcha-v3'
import PlanFeatures from './components/PlanFeatures'
import PaymentSummary from './components/PaymentSummary'
import PaymentTabs from './components/PaymentTabs'
import { getAdditionalTeamMemberPrice, getTeamMemberPriceIdBySymbol } from 'constants/plans'
import { useHistory } from 'react-router-dom'
import CustomModal from 'components/CustomModal/CustomModal'
import { showUpgradeModal } from 'Manage/actions'
import { isIndia } from '../../constants/currency'
import { PERIOD_YEAR } from '../../constants/plans'
import styles from './PaymentModal.module.css'

const getErrorMessagePayment = ({ declineCode = '', errorCode = '', accountAlreadyExistsMessage }) => {
  if (accountAlreadyExistsMessage) return accountAlreadyExistsMessage

  return (
    ERROR_CODE_MAP[declineCode] ||
    `Sorry, unable to process your card. Please try again <p style="font-size: 10px; color: #4c4c4c">(${errorCode} ${declineCode})</p>`
  )
}

const getOnClickAction = ({ dispatch, accountAlreadyExistsMessage }) => {
  if (accountAlreadyExistsMessage) return () => (window.location.href = '/login')

  return () => dispatch(transactionInit())
}

const PaymentModal = ({
  show,
  onHide,
  dispatch,
  declineCode,
  errorCode,
  transactionState,
  email,
  studentData,
  plan,
  periodProp = 'monthly',
  source,
  accountAlreadyExistsMessage,
  accountCreated,
  userProfile,
  onCurrencySelected,
  isTrial: isFreeAccount,
  extraTeamMembers = 0,
  currentCurrency = '$',
  stripeOnly = false,
  preferredTab = null
}) => {
  const [period, setPeriod] = useState(periodProp)
  const hasUcc = userProfile?.ucc
  const isLoading = transactionState === TRANSACTION_PROCESSING
  const isError = transactionState === TRANSACTION_FAILED
  const priceData = plan.prices[period] || {}
  const basePrice = priceData.value || 0
  const additionalTeamMemberPrice = getAdditionalTeamMemberPrice(currentCurrency)
  const totalCostForTeamMembers = extraTeamMembers * additionalTeamMemberPrice
  const [extraTeamMembersCost, setExtraTeamMembersCost] = useState(totalCostForTeamMembers)
  const [currentTeamMemberPriceId, setCurrentTeamMemberPriceId] = useState(
    getTeamMemberPriceIdBySymbol(currentCurrency)
  )
  const showExtra = extraTeamMembers > 0
  const total = basePrice + (showExtra ? extraTeamMembersCost : 0)
  const [errorMessage, setErrorMessage] = useState('')
  const [discountPercent, setDiscountPercent] = useState()
  // Must match PaymentTabs.js's own guard on the paddle-billing pane — if that pane
  // won't actually render (API plan, team add-on, or a UCC/referral code), defaulting
  // to it would point Tab.Container at a non-existent pane and show no form at all.
  const paddleTabAvailable = !stripeOnly && plan.id !== PLANS.API.id && !showExtra && !hasUcc
  const [activePaymentTab, setActivePaymentTab] = useState(
    preferredTab === 'paddle-billing' && paddleTabAvailable ? 'paddle-billing' : 'card'
  )
  const history = useHistory()

  useEffect(() => {
    // Force annual pricing for Indian users
    if (isIndia) {
      setPeriod(PERIOD_YEAR)
    } else {
      setPeriod(periodProp)
    }
  }, [periodProp])

  useEffect(() => {
    if (extraTeamMembers > 0) {
      const newPrice = getAdditionalTeamMemberPrice(currentCurrency)
      const newPriceId = getTeamMemberPriceIdBySymbol(currentCurrency)
      setExtraTeamMembersCost(extraTeamMembers * newPrice)
      setCurrentTeamMemberPriceId(newPriceId)
    }
  }, [currentCurrency, extraTeamMembers])

  const handleCouponCodeChange = (e) => {
    setDiscountPercent(e)
  }

  const handleDurationChange = (duration) => {
    setPeriod(duration)
    setErrorMessage('')
  }

  const showCurrencyDropdown = isFreeAccount && plan.id !== PLANS.API.id

  const handleCurrencySelected = (currency) => {
    if (onCurrencySelected) {
      onCurrencySelected(currency)
    }
  }

  const handleComparePlans = () => {
    dispatch(showUpgradeModal(true))
  }

  const onClose = () => {
    const { pathname } = window.location
    if (pathname === '/manage/subscription/upgrade' || pathname === '/manage/subscription/change') {
      history.push('/manage')
    }

    if (onHide) onHide()
  }

  return (
    <CustomModal
      backdrop="static"
      size="lg"
      show={show}
      onHide={onClose}
      aria-labelledby="contained-modal-title-vcenter"
      dialogClassName={styles.dialog}
      centered
      enforceFocus={false}
      mobileCloseButton={false}
    >
      <Container fluid>
        <Row>
          <PlanFeatures plan={plan} onClose={onClose} handleComparePlans={handleComparePlans} />
          <Col lg={7} className={`p-4 ${styles.paymentContainer}`}>
            <div>
              <span className="text-dark-blue font-20 text-700">Choose a payment plan:</span>
              <img
                alt="delete"
                onClick={onClose}
                className="link float-right d-none d-lg-block"
                src="/delete.png"
                height="20"
              />
            </div>
            <div className="position-relative">
              <PaymentSummary
                plan={plan}
                period={period}
                onPeriodChange={handleDurationChange}
                isLoading={isLoading}
                isError={isError}
                showExtra={showExtra}
                currentCurrency={currentCurrency}
                basePrice={basePrice}
                extraTeamMembers={extraTeamMembers}
                extraTeamMembersCost={extraTeamMembersCost}
                total={total}
                showCurrencyDropdown={showCurrencyDropdown}
                onCurrencySelected={handleCurrencySelected}
                discountPercent={discountPercent}
                isIndia={isIndia}
              />

              <PaymentTabs
                plan={plan}
                period={period}
                priceData={priceData}
                isLoading={isLoading}
                isError={isError}
                hasUcc={hasUcc}
                showExtra={showExtra}
                dispatch={dispatch}
                email={email}
                studentData={studentData}
                source={source}
                accountCreated={accountCreated}
                userProfile={userProfile}
                handleCouponCodeChange={handleCouponCodeChange}
                errorMessage={errorMessage}
                setErrorMessage={setErrorMessage}
                extraTeamMembers={extraTeamMembers}
                teamMemberPriceId={currentTeamMemberPriceId}
                transactionState={transactionState}
                activePaymentTab={activePaymentTab}
                setActivePaymentTab={setActivePaymentTab}
                stripeOnly={stripeOnly}
              />
              <LoadingBox className={isLoading ? 'd-block' : 'd-none'} message="Processing" />
              {isError && (
                <ErrorBox
                  className="d-block"
                  message={getErrorMessagePayment({
                    errorCode,
                    declineCode,
                    accountAlreadyExistsMessage
                  })}
                  onClick={getOnClickAction({ dispatch, accountAlreadyExistsMessage })}
                  secondaryButton={
                    !stripeOnly && activePaymentTab === 'card' && !hasUcc && !showExtra && !accountAlreadyExistsMessage
                  }
                  secondaryLabel="Try alternative method?"
                  secondaryBtnLabel="Checkout with Paddle"
                  secondaryOnClick={() => {
                    dispatch(transactionInit())
                    setActivePaymentTab('paddle-billing')
                  }}
                />
              )}
            </div>
          </Col>
        </Row>

        {/* Only on mobile */}
        <div className="d-lg-none op-6 font-small ps-3 pe-3 pb-3">
          By using our service you accept our{' '}
          <a href="https://tiiny.host/termsofservice.html" target="_blank" rel="noopener noreferrer">
            <u>terms & conditions</u>
          </a>{' '}
          &{' '}
          <a href="https://tiiny.host/privacypolicy.html" target="_blank" rel="noopener noreferrer">
            <u>privacy policy</u>
          </a>
        </div>
      </Container>
    </CustomModal>
  )
}

const mapDispatchToProps = (dispatch) => ({ dispatch })
const mapStateToProps = ({ pricing, manage }) => ({
  accountCreated: manage?.userProfile?.created,
  userProfile: manage?.userProfile,
  transactionState: pricing?.transactionState,
  declineCode: pricing?.declineCode,
  errorCode: pricing?.errorCode,
  accountAlreadyExistsMessage: pricing?.accountAlreadyExistsMessage,
  isTrial: !manage?.userProfile?.productId
})

export default connect(mapStateToProps, mapDispatchToProps)(withGoogleReCaptcha(PaymentModal))
